import { useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Icon, type IconName } from '@/common/ui/Icon'

export type ChoiceGroupItem = {
    label?: string
    iconName?: IconName
}

export type ChoiceGroupProps = {
    className?: string
    activeIndex?: number
    items: Array<string | ChoiceGroupItem>
    onChange?: (index: number) => void
}

export const ChoiceGroup = ({ className, activeIndex = 0, items, onChange }: ChoiceGroupProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
    const [indicatorStyle, setIndicatorStyle] = useState<{ width: number; transform: string } | null>(null)

    useLayoutEffect(() => {
        const container = containerRef.current
        const activeItem = itemRefs.current[activeIndex]
        if (!container || !activeItem) return

        const containerRect = container.getBoundingClientRect()
        const itemRect = activeItem.getBoundingClientRect()
        const width = itemRect.width
        const offsetX = itemRect.left - containerRect.left

        setIndicatorStyle({
            width,
            transform: `translateX(${offsetX}px)`,
        })
    }, [activeIndex, items])

    useLayoutEffect(() => {
        const handleResize = () => {
            const container = containerRef.current
            const activeItem = itemRefs.current[activeIndex]
            if (!container || !activeItem) return

            const containerRect = container.getBoundingClientRect()
            const itemRect = activeItem.getBoundingClientRect()
            setIndicatorStyle({
                width: itemRect.width,
                transform: `translateX(${itemRect.left - containerRect.left}px)`,
            })
        }

        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [activeIndex])

    return (
        <div
            ref={containerRef}
            className={cn(
                'bg-[rgba(255,255,255,0.05)] flex items-center justify-center rounded-[10px] relative',
                className
            )}
        >
            {indicatorStyle && (
                <span
                    className='absolute left-0 top-0 h-full rounded-[10px] bg-[rgba(255,255,255,0.1)] transition-transform duration-200 ease-out'
                    style={{ width: indicatorStyle.width, transform: indicatorStyle.transform }}
                />
            )}
            {items.map((item, index) => {
                const normalizedItem: ChoiceGroupItem = typeof item === 'string' ? { label: item } : item
                const isActive = index === activeIndex
                return (
                    <button
                        key={`${normalizedItem.label}-${index}`}
                        type='button'
                        className={cn(
                            'flex items-center justify-center gap-[8px] px-[12px] py-3 overflow-clip transition-colors',
                            'cursor-pointer relative z-[1]'
                        )}
                        onClick={() => onChange?.(index)}
                        aria-pressed={isActive}
                        ref={element => {
                            itemRefs.current[index] = element
                        }}
                    >
                        {normalizedItem.iconName && <Icon name={normalizedItem.iconName} size='sm' />}
                        {normalizedItem.label && (
                            <p
                                className={cn(
                                    'text-[16px] leading-[16px] font-normal',
                                    isActive ? 'text-white' : 'text-[#aba7af]'
                                )}
                            >
                                {normalizedItem.label}
                            </p>
                        )}
                    </button>
                )
            })}
        </div>
    )
}
